import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Gauge, RefreshCw, Server, HardDrive, ShieldCheck, Scale, KeyRound, Network, Camera, Archive, Box, type LucideIcon } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Stat, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardBody, Button, Skeleton } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { formatDate } from '@/lib/format-date';
import {
  useInstanceList,
  useVolumes,
  useQuotas,
  useQuotaViewers,
  useRefreshQuotaViewers,
  useResourceUsages,
  type UsageRow,
} from '@/api/opennebula';
import { RangeControl, RefreshButton, type PeriodOpt } from '../_shared/AnalyticsKit';
import { type TimeRange } from '@/api/manticore';
import { TableSkeleton, ErrorState } from './components/ListPageShell';

const VIEWER_ICONS: Record<string, LucideIcon> = {
  instances: Server, volumes: HardDrive, secgroups: ShieldCheck, lbs: Scale,
  sshkeys: KeyRound, networks: Network, snapshots: Camera, backups: Archive,
};

/** Commitment kinds — include load balancers so LB usage is billed (matches @console). */
const USAGE_COMMITMENTS = ['virtual-machine', 'load-balancer'];

/** Usage is a monthly billing view, so the presets are calendar months (mirrors the
 *  legacy Usages screen); the shared analytics RangeControl adds a custom picker. */
const USAGE_PERIODS: PeriodOpt[] = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
];

/**
 * Resolve the selected range to the `{gte,lte}` (date-only) window the `/usages`
 * worker expects. Month presets cover the WHOLE calendar month — running resources
 * accrue through the period end, terminated ones stop at termination — matching the
 * legacy billing view; a custom pick uses the chosen day bounds.
 */
function usageRangeFor(range: TimeRange): { gte: string; lte: string } {
  const fmt = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  // Use the LOCAL calendar day of each picked instant, not `.slice(0,10)` of the
  // UTC ISO string — for east-of-UTC timezones local midnight is the previous day
  // in UTC, which would shift the window a day earlier (e.g. 5–12 Sep → 4–12 Sep,
  // billing 9 days/216h instead of 8/192h).
  if (range.kind === 'custom') return { gte: fmt(new Date(range.from)), lte: fmt(new Date(range.to)) };
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  // new Date(y, m + 1, 0) = last day of month m (day 0 of the next month).
  return range.period === 'last_month'
    ? { gte: fmt(new Date(y, m - 1, 1)), lte: fmt(new Date(y, m, 0)) }
    : { gte: fmt(new Date(y, m, 1)), lte: fmt(new Date(y, m + 1, 0)) };
}

export function QuotasUsage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: quotas = {}, isLoading: qLoading } = useQuotas(serviceId);
  const { data: viewers } = useQuotaViewers(serviceId);
  const { data: instances = [] } = useInstanceList(serviceId);
  const { data: volumes = [] } = useVolumes(serviceId);
  const [range, setRange] = useState<TimeRange>({ kind: 'preset', period: 'this_month' });
  const query = useMemo(() => ({ ...usageRangeFor(range), commitments: USAGE_COMMITMENTS }), [range]);
  const { data: usages = [], isLoading: uLoading, isError: uError, refetch } = useResourceUsages(serviceId, query);

  const refreshViewers = useRefreshQuotaViewers(serviceId!, {
    onSuccess: () => show({ tone: 'success', title: 'Resource counts refreshed' }),
    onError: () => show({ tone: 'danger', title: 'Refresh failed' }),
  });

  // Live current usage = sum of each instance's reserved resources, plus the size
  // of unattached standalone volumes (mirrors @console `useResourceCalculator`).
  const usage = useMemo(() => {
    const sum = (pick: (r: NonNullable<NonNullable<(typeof instances)[number]['metadata']>['resources']>) => number | undefined) =>
      instances.reduce((acc, i) => acc + (Number(pick(i.metadata?.resources ?? {})) || 0), 0);
    const extraVolume = volumes
      .filter((v) => !v.attached?.instance_id)
      .reduce((acc, v) => acc + Number(v.size ?? 0) / 1024, 0);
    return {
      cpu: sum((r) => r.cores),
      memory: sum((r) => r.memory),
      volume: Math.round((sum((r) => r.volumes) + extraVolume) * 10) / 10,
      network: sum((r) => r.networks),
    };
  }, [instances, volumes]);

  const viewerCount = (key: string) => viewers?.viewers.find((v) => v.key === key)?.value;

  const quotaCards: { label: string; current?: number; value?: number; unit?: string; unlimited?: boolean }[] = [
    { label: 'vCPU', current: usage.cpu, value: quotas.cores },
    { label: 'Memory', current: usage.memory, value: quotas.memory, unit: 'GiB' },
    { label: 'Volume', current: usage.volume, value: quotas.volumes, unit: 'GiB' },
    { label: 'Network', current: usage.network, value: quotas.networks },
    { label: 'Instances', current: instances.length, value: quotas.instances },
    { label: 'Backups', current: viewerCount('backups'), value: quotas.backups },
    { label: 'Snapshots', current: viewerCount('snapshots'), value: quotas.snapshots },
    { label: 'Load Balancers', current: viewerCount('lbs'), unlimited: true },
  ];

  const columns: ColumnDef<UsageRow, unknown>[] = [
    // instance_id is the instance's platform UUID (same id the Activity log and
    // instance routes use) — show a short prefix with the full id on hover.
    { id: 'id', header: 'ID', cell: ({ row }) => {
      const id = row.original.instance_id;
      return id
        ? <code title={id} style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{String(id).slice(0, 8)}</code>
        : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
    } },
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.instance_name || row.original.instance_id || '—'}</span> },
    { id: 'state', header: 'Status', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', textTransform: 'capitalize' }}>{String(row.original.instance_state ?? '').toLowerCase() || '—'}</span> },
    { id: 'cpu', header: 'vCPU', cell: ({ row }) => row.original.instance_cpu ?? '—' },
    { id: 'ram', header: 'RAM (GB)', cell: ({ row }) => row.original.instance_ram ?? '—' },
    { id: 'volume', header: 'Volume (GB)', cell: ({ row }) => row.original.instance_volume ?? '—' },
    { id: 'image', header: 'OS', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.instance_image || '—'}</span> },
    { id: 'ip', header: 'IP', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.instance_floating_ip || row.original.instance_assigned_ip || '—'}</span> },
    { id: 'duration', header: 'Hours', cell: ({ row }) => row.original.instance_duration ?? '—' },
    { id: 'start', header: 'Start billing', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.instance_duration_start ? formatDate(row.original.instance_duration_start) : '—'}</span> },
    { id: 'end', header: 'End billing', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.instance_duration_end ? formatDate(row.original.instance_duration_end) : '—'}</span> },
    { id: 'created', header: 'Created at', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.instance_created_at ? formatDate(row.original.instance_created_at) : '—'}</span> },
  ];

  return (
    <Container size="xl">
      <PageHeader
        icon={<Gauge size={22} />}
        title="Quotas & usage"
        description="Resource limits, live counts and per-instance consumption for this service."
      />
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section title="Quotas" description="Resource limits and live consumption for this service." style={{ marginBottom: 0 }}>
        {qLoading ? (
          <Skeleton style={{ height: 96 }} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--vn-space-3)' }}>
            {quotaCards.map((q) => (
              <Card key={q.label} flat>
                <CardBody>
                  <Stat
                    label={q.label}
                    value={
                      q.unlimited
                        ? q.current != null ? String(q.current) : '—'
                        : q.current != null && q.value != null
                          ? `${q.current} / ${q.value}`
                          : q.value != null
                            ? String(q.value)
                            : q.current != null
                              ? String(q.current)
                              : '—'
                    }
                    hint={q.unit}
                  />
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </Section>

      {viewers && viewers.viewers.length > 0 && (
        <Section
          title="Resource counts"
          style={{ marginBottom: 0 }}
          description={viewers.refreshed_at ? `Live counts · refreshed ${formatDate(viewers.refreshed_at)}` : 'Live counts of provisioned resources.'}
          actions={
            <Button size="sm" variant="secondary" startIcon={<RefreshCw size={14} />} disabled={refreshViewers.isPending} onClick={() => refreshViewers.mutate()}>
              Refresh
            </Button>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' }}>
            {viewers.viewers.map((v) => {
              const Icon = VIEWER_ICONS[v.key] ?? Box;
              return (
                <Card key={v.key} flat>
                  <CardBody>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-2)' }}>
                      <Icon size={16} /> <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{v.name}</span>
                    </div>
                    <div style={{ fontSize: 'var(--vn-font-size-3xl)', fontWeight: 'var(--vn-font-weight-bold)', opacity: v.disabled ? 0.4 : 1 }}>{v.value}</div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </Section>
      )}

      <Section
        title="Resource usage"
        description="Per-instance consumption over the selected period."
        style={{ marginBottom: 0 }}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            <RangeControl options={USAGE_PERIODS} value={range} onChange={setRange} />
            <RefreshButton onRefresh={() => refetch()} />
          </div>
        }
      >
        {uLoading ? (
          <TableSkeleton />
        ) : uError ? (
          <ErrorState description="Could not load usage data for the selected period." onRetry={() => refetch()} />
        ) : (
          // DataTable owns the bordered frame; the empty placeholder renders
          // inside it via `emptyState`, so empty and populated share one frame
          // (no extra Card wrapper, no double border).
          <DataTable
            columns={columns}
            data={usages}
            // An instance can have several billing segments in one period, so
            // instance_id alone is not unique — qualify with the row index.
            getRowId={(u, i) => `${u.instance_id ?? 'row'}-${i}`}
            emptyState={
              <EmptyState icon={<Gauge size={28} />} title="No usage data" description="No billable usage for the selected period." />
            }
          />
        )}
      </Section>
    </div>
    </Container>
  );
}
